import React, { useState, useEffect } from 'react';
import axios from 'axios';
import TypeWriter from '../components/TypeWriter';
import './SOS.css';
import EmergencyOverlay from './EmergencyOverlay';

const SOS = () => {
  const [contacts, setContacts] = useState([]);
  const [showContactForm, setShowContactForm] = useState(false);
  const [newContact, setNewContact] = useState({ name: '', phone_number: '' });
  const [loading, setLoading] = useState(false);
  const [sosLoading, setSosLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showEmergencyOverlay, setShowEmergencyOverlay] = useState(false);
  const [componentError, setComponentError] = useState(null);

  useEffect(() => {
    try {
      fetchContacts();
    } catch (err) {
      console.error('Error in useEffect:', err);
      setComponentError('Failed to initialize SOS page');
    }
  }, []);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const response = await axios.get('https://bharathchilaka-test.hf.space/api/contacts');
      
      // Ensure response.data is an array
      if (Array.isArray(response.data)) {
        setContacts(response.data);
      } else {
        console.warn('Unexpected response format:', response.data);
        setContacts([]);
      }
    } catch (err) {
      console.error('Error fetching contacts:', err);
      setError('Failed to load contacts. Please check your internet connection.');
      setContacts([]); // Set empty array as fallback
    } finally {
      setLoading(false);
    }
  };

  const handleSOS = async () => {
    if (contacts.length === 0) {
      setError('Please add emergency contacts first');
      return;
    }

    setSosLoading(true);
    setShowEmergencyOverlay(true);
    setError('');
    setSuccess('');

    try {
      // Get current location
      if (!navigator.geolocation) {
        throw new Error('Geolocation is not supported by your browser');
      }

      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        });
      });

      const { latitude, longitude } = position.coords;

      const response = await axios.post('https://bharathchilaka-test.hf.space/api/sos', {
        user_name: 'SignCrypt User',
        latitude: latitude,
        longitude: longitude
      });
      
      setSuccess(`SOS alert sent successfully to ${response.data.contacts_notified} contacts`);
      
      // Hide emergency overlay after 3 seconds
      setTimeout(() => {
        setShowEmergencyOverlay(false);
      }, 3000);
      
    } catch (err) {
      if (err.code === 1) {
        setError('Location access denied. Please enable location services and try again.');
      } else if (err.code === 2) {
        setError('Location unavailable. Please check your internet connection.');
      } else if (err.code === 3) {
        setError('Location request timed out. Please try again.');
      } else {
        setError(err.response?.data?.error || 'Failed to send SOS alert');
      }
      
      // Hide emergency overlay after showing error
      setTimeout(() => {
        setShowEmergencyOverlay(false);
      }, 3000);
      
    } finally {
      setSosLoading(false);
    }
  };

  const handleAddContact = async (e) => {
    e.preventDefault();
    
    if (!newContact.name.trim() || !newContact.phone_number.trim()) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post('https://bharathchilaka-test.hf.space/api/contacts', newContact);
      setContacts(prev => [...prev, response.data.contact]);
      setNewContact({ name: '', phone_number: '' });
      setShowContactForm(false);
      setSuccess('Contact added successfully');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add contact');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await axios.delete(`https://bharathchilaka-test.hf.space/api/contacts/${contactId}`);
      setContacts(prev => prev.filter(contact => contact.id !== contactId));
      setSuccess('Contact deleted successfully');
    } catch (err) {
      setError('Failed to delete contact');
    }
  };

  // Error boundary for component errors
  if (componentError) {
    return (
      <div className="sos-container">
        <div className="container">
          <div className="error-message">
            <h2>⚠️ Error Loading SOS Page</h2>
            <p>{componentError}</p>
            <button onClick={() => window.location.reload()}>Reload Page</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sos-container">
      <EmergencyOverlay 
        isActive={showEmergencyOverlay} 
        onClose={() => setShowEmergencyOverlay(false)} 
      />
      <div className="container">
        
        {/* Debug Info (remove in production) */}
        <div style={{ display: 'none' }}>
          Debug: contacts={contacts?.length || 0}, loading={loading.toString()}, error={error}
        </div>
        {/* Header */}
        <div className="sos-header card" data-aos="fade-up" data-aos-duration="900">
          <h1>
            <TypeWriter 
              text="🚨 Emergency SOS" 
              speed={100} 
              cursorColor="white"
            />
          </h1>
          <p>Quick access to emergency assistance and contact management</p>
        </div>

        {/* SOS Button */}
        <div className="sos-button-section card" data-aos="zoom-in" data-aos-delay="100" data-aos-duration="800">
          <h2>Emergency Alert</h2>
          <p>Click the button below to send an SOS alert to all your emergency contacts</p>
          
          <button 
            className={`sos-emergency-btn ${sosLoading ? 'loading' : ''}`}
            onClick={handleSOS}
            disabled={sosLoading || contacts.length === 0}
          >
            {sosLoading ? 'Sending...' : '🚨 SEND SOS ALERT'}
            <div className="sos-ripple-effect"></div>
          </button>
          
          {contacts.length === 0 && (
            <p className="no-contacts-warning" data-aos="fade-in" data-aos-delay="200">
              ⚠️ Please add emergency contacts below before using SOS
            </p>
          )}
        </div>

        {/* Emergency Contacts */}
        <div className="contacts-section card" data-aos="fade-up" data-aos-delay="200" data-aos-duration="800">
          <div className="contacts-header">
            <h2>Emergency Contacts</h2>
            <button 
              className="btn btn-primary"
              onClick={() => setShowContactForm(true)}
              data-aos="zoom-in"
              data-aos-delay="250"
            >
              + Add Contact
            </button>
          </div>

          {contacts.length === 0 ? (
            <div className="no-contacts" data-aos="fade-in" data-aos-delay="300">
              <p>No emergency contacts added yet.</p>
              <p>Add contacts to enable SOS functionality.</p>
            </div>
          ) : (
            <div className="contacts-list">
              {contacts.map((contact, idx) => (
                <div key={contact.id} className="contact-item" data-aos="fade-up" data-aos-delay={350 + idx * 50}>
                  <div className="contact-info">
                    <h4>{contact.name}</h4>
                    <p>{contact.phone_number}</p>
                  </div>
                  <button 
                    className="delete-btn"
                    onClick={() => handleDeleteContact(contact.id)}
                    title="Delete Contact"
                  >
                    🗑️
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add Contact Form */}
        {showContactForm && (
          <div className="contact-form-modal" data-aos="zoom-in" data-aos-delay="400">
            <div className="contact-form-content">
              <h3>Add Emergency Contact</h3>
              
              <form onSubmit={handleAddContact}>
                <div className="form-group">
                  <label>Name</label>
                  <input
                    type="text"
                    value={newContact.name}
                    onChange={(e) => setNewContact(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Enter contact name"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Phone Number</label>
                  <input
                    type="tel"
                    value={newContact.phone_number}
                    onChange={(e) => setNewContact(prev => ({ ...prev, phone_number: e.target.value }))}
                    placeholder="Enter phone number"
                    required
                  />
                </div>
                
                <div className="form-actions">
                  <button 
                    type="button" 
                    className="btn btn-secondary"
                    onClick={() => setShowContactForm(false)}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? 'Adding...' : 'Add Contact'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Messages */}
        {error && (
          <div className="message error fade-in" data-aos="fade-in" data-aos-delay="600">
            {error}
          </div>
        )}
        
        {success && (
          <div className="message success fade-in" data-aos="fade-in" data-aos-delay="650">
            {success}
          </div>
        )}
      </div>
    </div>
  );
};

export default SOS;